import { useEffect, useRef, useState } from 'react';

export const useScrollAnimation = (options = {}) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Once visible, stop observing (animation plays once)
                    if (ref.current) {
                        observer.unobserve(ref.current);
                    }
                }
            },
            {
                threshold: options.threshold || 0.1,
                rootMargin: options.rootMargin || '0px 0px -50px 0px'
            }
        );
        
        if (ref.current) {
            observer.observe(ref.current);
        }
        
        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [options.threshold, options.rootMargin]);
    
    return [ref, isVisible];
};

// Component wrapper for scroll animations
export const ScrollReveal = ({ 
    children, 
    animation = 'fade-up', // 'fade-up', 'fade-left', 'fade-right', 'scale'
    delay = 0,
    className = '',
    ...props 
}) => {
    const [ref, isVisible] = useScrollAnimation();
    
    const animationClass = {
        'fade-up': 'animate-on-scroll',
        'fade-left': 'animate-on-scroll-left',
        'fade-right': 'animate-on-scroll-right',
        'scale': 'animate-on-scroll-scale'
    }[animation] || 'animate-on-scroll';
    
    return (
        <div 
            ref={ref} 
            className={`${animationClass} ${isVisible ? 'visible' : ''} ${className}`}
            style={{ transitionDelay: `${delay}s` }}
            {...props}
        >
            {children}
        </div>
    );
};

export default useScrollAnimation;
